import { use, useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import API from 'src/services/API';
import { TENANT_STORAGE_KEY } from 'src/constants/global-constant';

import { useAuthContext } from 'src/auth/hooks';
import { JWT_STORAGE_KEY } from 'src/auth/context/jwt/constant';
import { jwtDecode, setSession } from 'src/auth/context/jwt/utils';

import { SelectedTenantContext, type SelectedTenantContextValue } from './selected-tenant-context';

// ----------------------------------------------------------------------

/** 我的租户列表 Query Key（切换租户后可 invalidate） */
export const MY_TENANTS_QUERY_KEY = ['layout', 'my-tenants'] as const;

type SelectedTenantProviderProps = {
  children: React.ReactNode;
};

/** 从 accessToken 解析并同步 X-Tenant-Id 请求头用的租户主键 */
function readTenantIdFromStorage(): string {
  const cached = localStorage.getItem(TENANT_STORAGE_KEY);
  if (cached) return cached;

  const token = localStorage.getItem(JWT_STORAGE_KEY);
  if (!token) return '';

  try {
    const decoded = jwtDecode(token) as { tenantId?: string };
    return decoded?.tenantId ?? '';
  } catch {
    return '';
  }
}

/** Dashboard 当前租户：Header WorkspacesPopover 与多租户请求上下文共用 */
export function SelectedTenantProvider({ children }: SelectedTenantProviderProps) {
  const queryClient = useQueryClient();
  const { checkUserSession } = useAuthContext();
  const [tenantId, setTenantIdState] = useState(readTenantIdFromStorage);

  const { data: tenants = [], isLoading: loading } = useQuery({
    queryKey: MY_TENANTS_QUERY_KEY,
    queryFn: () => API.Auth.AuthControllerListMyTenantsV1(),
    select: (res) => res.data?.data ?? [],
  });

  // 列表加载后校验缓存 tenantId，无效则回退默认租户
  useEffect(() => {
    if (loading || tenants.length === 0) return;

    const valid = tenants.find((t) => t.tenantId === tenantId);
    const defaultTenant = tenants.find((t) => t.isDefault) ?? tenants[0];
    const next = valid?.tenantId ?? defaultTenant?.tenantId ?? '';

    if (next && next !== tenantId) {
      setTenantIdState(next);
      localStorage.setItem(TENANT_STORAGE_KEY, next);
    } else if (next && !localStorage.getItem(TENANT_STORAGE_KEY)) {
      localStorage.setItem(TENANT_STORAGE_KEY, next);
    }
  }, [loading, tenants, tenantId]);

  const switchMutation = useMutation({
    mutationFn: (targetTenantId: string) =>
      API.Auth.AuthControllerSwitchTenantV1(
        { tenantId: targetTenantId },
        {
          fetchOptions: {
            successMessage: '已切换租户',
          },
        }
      ),
    onSuccess: async (res, targetTenantId) => {
      const result = res.data?.data;
      const accessToken = result?.accessToken;
      const refreshToken = result?.refreshToken;

      if (accessToken) {
        await setSession(accessToken, refreshToken);
        localStorage.setItem(TENANT_STORAGE_KEY, targetTenantId);
      }

      setTenantIdState(targetTenantId);
      await checkUserSession?.();
      await queryClient.invalidateQueries();
    },
  });

  const handleChange = useCallback((id: string) => {
    setTenantIdState(id);
    localStorage.setItem(TENANT_STORAGE_KEY, id);
  }, []);

  const switchTenant = useCallback(
    async (targetTenantId: string) => {
      if (targetTenantId === tenantId) return;
      setTenantIdState(targetTenantId);
      await switchMutation.mutateAsync(targetTenantId);
    },
    [tenantId, switchMutation]
  );

  const membership = useMemo(
    () => tenants.find((t) => t.tenantId === tenantId),
    [tenants, tenantId]
  );

  const value = useMemo<SelectedTenantContextValue>(
    () => ({
      tenants,
      tenantId,
      membership,
      loading,
      switching: switchMutation.isPending,
      setTenantId: handleChange,
      switchTenant,
    }),
    [tenants, tenantId, membership, loading, switchMutation.isPending, handleChange, switchTenant]
  );

  return <SelectedTenantContext value={value}>{children}</SelectedTenantContext>;
}

/** 读取当前选中租户（须在 SelectedTenantProvider 内使用） */
export function useSelectedTenant() {
  const context = use(SelectedTenantContext);

  if (!context) {
    throw new Error('useSelectedTenant: must be used within SelectedTenantProvider');
  }

  return context;
}

/** 弹框等可能渲染在 Provider 外的场景，读不到则返回 undefined */
export function useOptionalSelectedTenant() {
  return use(SelectedTenantContext);
}
